import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Link2, Copy, ExternalLink, Globe, Home, Users, KeyRound,
  ShoppingBag, Building2, Zap, Info, Check,
} from 'lucide-react';
import api from '@/api';
import { useTenant } from '@/contexts/TenantContext';

/**
 * Pagina "Link Utili" — mostra al tenant tutti gli URL pubblici che può
 * condividere con clienti, ospiti, fornitori. Ogni link ha:
 *  - versione standard (app.shangrilux.com)
 *  - versione con dominio custom (se configurato)
 *  - pulsante copia negli appunti
 *  - pulsante apri in nuova scheda
 */

function LinkRow({ icon: Icon, label, url, description, hint }) {
  const [copied, setCopied] = useState(false);
  const doCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copiato');
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="p-3.5 rounded-sm" data-testid={`useful-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-gold)' }}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{label}</p>
          </div>
          {description && (
            <p className="text-[11px] mb-2" style={{ color: 'var(--lux-text-muted-2)' }}>{description}</p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-[240px] px-2.5 py-1.5 text-[11px] rounded-sm break-all font-mono"
              style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-gold-soft, #d8bc6a)', border: '1px solid var(--lux-border-2)' }}>
              {url}
            </code>
            <button onClick={doCopy} data-testid="copy-link-btn"
              className="p-1.5 rounded-sm transition-colors" title="Copia"
              style={{ background: copied ? 'rgba(34,197,94,0.15)' : 'var(--lux-surface-3)', color: copied ? '#22c55e' : 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}>
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </button>
            <a href={url} target="_blank" rel="noopener noreferrer"
              className="p-1.5 rounded-sm transition-colors" title="Apri in nuova scheda"
              style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)', border: '1px solid var(--lux-border-2)' }}>
              <ExternalLink size={12} />
            </a>
          </div>
          {hint && (
            <p className="text-[10px] italic mt-2" style={{ color: 'var(--lux-text-muted-3)' }}>💡 {hint}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2.5">
      <h3 className="text-xs uppercase tracking-widest font-medium mb-1" style={{ color: 'var(--lux-gold)' }}>{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export default function UsefulLinks() {
  const { tenant } = useTenant();
  const [properties, setProperties] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);

  const customDomain = tenant?.custom_domain?.trim() || '';
  const stdBase = 'https://app.shangrilux.com';
  const customBase = customDomain ? `https://${customDomain.replace(/^https?:\/\//, '').replace(/\/$/, '')}` : '';

  useEffect(() => {
    (async () => {
      try {
        const [pRes, sRes, kRes] = await Promise.allSettled([
          api.get('/properties'),
          api.get('/suppliers'),
          api.get('/api-keys').catch(() => ({ data: { keys: [] } })),
        ]);
        if (pRes.status === 'fulfilled') setProperties(pRes.value.data.properties || pRes.value.data || []);
        if (sRes.status === 'fulfilled') setSuppliers(sRes.value.data.suppliers || sRes.value.data || []);
        if (kRes.status === 'fulfilled') setApiKeys(kRes.value.data.keys || []);
      } catch { /* silent */ }
    })();
  }, []);

  const buildUrl = (base, path) => `${base}${path}`;

  return (
    <div className="space-y-6" data-testid="useful-links-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center"
            style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--lux-gold)' }}>
            <Link2 size={18} />
          </div>
          <div>
            <h2 className="font-serif text-2xl" style={{ color: 'var(--lux-text)' }}>Link Utili</h2>
            <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>
              Tutti gli URL che puoi condividere con clienti, ospiti e fornitori
            </p>
          </div>
        </div>
        {customDomain && (
          <div className="mt-3 p-3 rounded-sm flex items-start gap-2 text-xs"
            style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)' }}>
            <Globe size={14} style={{ color: '#22c55e' }} className="shrink-0 mt-0.5" />
            <div style={{ color: 'var(--lux-text-muted-2)' }}>
              <b style={{ color: '#22c55e' }}>Dominio personalizzato attivo:</b> <code style={{ color: 'var(--lux-gold)' }}>{customDomain}</code>. I link qui sotto sono mostrati sia con il tuo dominio (consigliato per i clienti) sia con quello standard (backup).
            </div>
          </div>
        )}
        {!customDomain && (
          <div className="mt-3 p-3 rounded-sm flex items-start gap-2 text-xs"
            style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)' }}>
            <Info size={14} style={{ color: 'var(--lux-gold)' }} className="shrink-0 mt-0.5" />
            <div style={{ color: 'var(--lux-text-muted-2)' }}>
              Nessun dominio personalizzato configurato — i link usano <code style={{ color: 'var(--lux-gold)' }}>app.shangrilux.com</code>. Puoi attivare un dominio tuo dalla sezione <b>Branding → Dominio Custom</b>.
            </div>
          </div>
        )}
      </div>

      {/* Booking pubblico */}
      <Section title="Portale Prenotazioni (per ospiti / clienti)">
        <LinkRow
          icon={Home}
          label="Portale prenotazioni generale"
          url={buildUrl(customBase || stdBase, '/booking')}
          description="Link principale da mettere sul tuo sito, nella firma email, sui social."
          hint="Ideale come CTA 'Prenota ora'. Mostra tutte le tue proprietà."
        />
        {customBase && (
          <LinkRow
            icon={Home}
            label="Portale prenotazioni (URL standard)"
            url={buildUrl(stdBase, '/booking')}
            description="Backup con dominio Shangrilux — utile se il tuo dominio custom ha problemi."
          />
        )}
        {(properties || []).map(p => (
          <LinkRow
            key={p.slug}
            icon={Building2}
            label={`Prenotazione — ${p.name}`}
            url={buildUrl(customBase || stdBase, `/booking/${p.slug}`)}
            description={`Link diretto per prenotare specificatamente ${p.name}`}
            hint="Manda questo link a chi è interessato a una proprietà specifica — salta la selezione iniziale."
          />
        ))}
      </Section>

      {/* Panel admin */}
      <Section title="Accesso Amministrativo">
        <LinkRow
          icon={KeyRound}
          label="Login pannello admin"
          url={buildUrl(customBase || stdBase, '/panel/login')}
          description="Il tuo accesso al pannello di gestione. Salvalo tra i preferiti."
          hint="Se hai team, condividi questo link e crea utenti admin dedicati."
        />
      </Section>

      {/* Verify receipt (pubblico) */}
      <Section title="Verifica ricevute (link pubblico)">
        <LinkRow
          icon={Check}
          label="Verifica autenticità ricevuta"
          url={buildUrl(customBase || stdBase, '/verify-receipt')}
          description="I clienti possono verificare che una ricevuta sia autentica scansionando il QR sul PDF o aprendo questo link."
          hint="Aggiunge un tocco 'luxury' all'esperienza cliente — trust signal."
        />
      </Section>

      {/* Fornitori */}
      {suppliers && suppliers.length > 0 && (
        <Section title="Portali Fornitori (uno per fornitore)">
          {suppliers.slice(0, 10).map(s => (
            <LinkRow
              key={s.id || s._id || s.access_token}
              icon={ShoppingBag}
              label={s.name || 'Fornitore'}
              url={buildUrl(customBase || stdBase, `/fornitore/${s.access_token}`)}
              description={s.email || 'Portale privato per inserire preventivi'}
              hint="Manda questo link al fornitore via email/WhatsApp — è personale, nominale e sicuro."
            />
          ))}
          {suppliers.length > 10 && (
            <p className="text-xs italic pl-2" style={{ color: 'var(--lux-text-muted)' }}>
              …e altri {suppliers.length - 10} fornitori. Vai in <b>Fornitori</b> per gestirli tutti.
            </p>
          )}
        </Section>
      )}

      {/* Guest app (info) */}
      <Section title="App Ospite (link personalizzato per prenotazione)">
        <div className="p-3.5 rounded-sm"
          style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
              style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-gold)' }}>
              <Users size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--lux-text)' }}>Link ospite (per ogni prenotazione confermata)</p>
              <p className="text-[11px] mb-2" style={{ color: 'var(--lux-text-muted-2)' }}>
                Ogni prenotazione confermata genera un link unico dell&apos;ospite del tipo:
              </p>
              <code className="block px-2.5 py-1.5 text-[11px] rounded-sm mb-2 font-mono"
                style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-gold-soft)', border: '1px solid var(--lux-border-2)' }}>
                {(customBase || stdBase)}/g/&lt;TOKEN_OSPITE&gt;
              </code>
              <p className="text-[10px] italic" style={{ color: 'var(--lux-text-muted-3)' }}>
                💡 Il link contiene chiave digitale QR, dettagli soggiorno, servizi. Viene inviato automaticamente via email al momento della conferma. Puoi vedere/rinviare il link ospite dalla scheda di ogni prenotazione.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* API */}
      {apiKeys && apiKeys.length > 0 && (
        <Section title="Integrazioni tecniche (per il tuo team IT)">
          <LinkRow
            icon={Zap}
            label="API Base URL"
            url={buildUrl(customBase || stdBase, '/api')}
            description="Endpoint delle API pubbliche v1 — usa le tue chiavi API dalla sezione dedicata."
            hint="Documentazione completa e SDK Python/Node disponibili nella guida."
          />
        </Section>
      )}
    </div>
  );
}
